/**
 * Roteador da suíte (SPEC-v2 2.1, 9.1, 9.2): `wouter` com `nest` por área.
 *
 * - Público: `/entrar` (no bundle inicial), `/entrar/codigo`, `/configurar`, `/convite`, `/esqueci`
 *   e `/redefinir` (lazy). `/tv` fica fora do login de pessoa.
 * - Rotas antigas da V1 (`/hoje`, `/decisoes/:id`…) → `/painel/…` preservando a query, sem entrada
 *   no histórico (`replace`); `/ajustes/usuarios` → `/equipe/pessoas`.
 * - Atrás do `AuthGate`: `/` (lançador), `/painel/*`, `/automacoes/*`, `/estudio/*`, `/palco/*`, `/equipe/*` e
 *   `/conta/*`. Cada área é um chunk; área sem permissão → "Sem acesso".
 */
import type { Area } from '@rota/contracts';
import { type ComponentType, lazy, type ReactNode, Suspense } from 'react';
import { Redirect, Route, Switch, useLocation, useSearch } from 'wouter';
import { AREA_NAME } from '@/components/suite/copy';
import { canOpenArea, useSession } from '@/data/auth/session';
import { LoginPage } from '../auth/LoginPage';
import { devRoutes } from '../devRoutes';
import { RouteErrorBoundary } from '../RouteStates';
import { AuthGate } from './AuthGate';
import { legacyRedirect } from './areaRegistry';
import { StepUpProvider } from './StepUp';
import { SuiteNotice } from './SuiteNotice';

const TwoFactorPage = lazy(() => import('../auth/TwoFactorPage').then((m) => ({ default: m.TwoFactorPage })));
const SetupPage = lazy(() => import('../auth/SetupPage').then((m) => ({ default: m.SetupPage })));
const InvitePage = lazy(() => import('../auth/InvitePage').then((m) => ({ default: m.InvitePage })));
const ForgotPage = lazy(() => import('../auth/ForgotPage').then((m) => ({ default: m.ForgotPage })));
const ResetPage = lazy(() => import('../auth/ResetPage').then((m) => ({ default: m.ResetPage })));
const Launcher = lazy(() => import('./Launcher').then((m) => ({ default: m.Launcher })));
const EquipePage = lazy(() => import('../equipe/EquipePage').then((m) => ({ default: m.EquipePage })));
const ContaPage = lazy(() => import('../conta/ContaPage').then((m) => ({ default: m.ContaPage })));
const PainelArea = lazy(() => import('@/areas/painel/PainelArea').then((m) => ({ default: m.PainelArea })));
const TvArea = lazy(() => import('@/areas/painel/PainelArea').then((m) => ({ default: m.TvArea })));
const AutomacoesArea = lazy(() =>
  import('@/areas/automacoes/AutomacoesArea').then((m) => ({ default: m.AutomacoesArea })),
);
const EstudioArea = lazy(() =>
  import('@/areas/estudio/EstudioArea').then((m) => ({ default: m.EstudioArea })),
);
const PalcoArea = lazy(() => import('@/areas/palco/PalcoArea').then((m) => ({ default: m.PalcoArea })));

const AREAS: Record<Area, ComponentType> = {
  painel: PainelArea,
  automacoes: AutomacoesArea,
  estudio: EstudioArea,
  palco: PalcoArea,
};

/** Cada área ou página de topo: o erro de uma não derruba as outras; carregando = nada (abertura). */
function Boundary({ id, children }: { id: string; children: ReactNode }) {
  return (
    <RouteErrorBoundary resetKey={id}>
      <Suspense fallback={null}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

function AreaGate({ area }: { area: Area }) {
  const session = useSession();
  if (!canOpenArea(session, area)) return <SuiteNotice kind="no_access" areaName={AREA_NAME[area]} />;
  const Area = AREAS[area];
  return (
    <Boundary id={area}>
      <Area />
    </Boundary>
  );
}

/** Rota antiga → `/painel/…` (ou 404 da suíte quando não é rota conhecida). */
function LegacyOrNotFound() {
  const [location] = useLocation();
  const search = useSearch();
  const to = legacyRedirect(location, search, typeof window === 'undefined' ? '' : window.location.hash);
  if (to) return <Redirect to={to} replace />;
  return (
    <AuthGate>
      <SuiteNotice kind="not_found" />
    </AuthGate>
  );
}

export function SuiteRouter() {
  return (
    <StepUpProvider>
      <Switch>
        <Route path="/entrar">
          <LoginPage />
        </Route>
        <Route path="/entrar/codigo">
          <Boundary id="2fa">
            <TwoFactorPage />
          </Boundary>
        </Route>
        <Route path="/configurar">
          <Boundary id="setup">
            <SetupPage />
          </Boundary>
        </Route>
        <Route path="/convite">
          <Boundary id="convite">
            <InvitePage />
          </Boundary>
        </Route>
        <Route path="/esqueci">
          <Boundary id="esqueci">
            <ForgotPage />
          </Boundary>
        </Route>
        <Route path="/redefinir">
          <Boundary id="redefinir">
            <ResetPage />
          </Boundary>
        </Route>
        <Route path="/tv">
          <Boundary id="tv">
            <TvArea />
          </Boundary>
        </Route>
        {devRoutes()}
        <Route path="/">
          <AuthGate>
            <Boundary id="lancador">
              <Launcher />
            </Boundary>
          </AuthGate>
        </Route>
        <Route path="/painel" nest>
          <AuthGate>
            <AreaGate area="painel" />
          </AuthGate>
        </Route>
        <Route path="/automacoes" nest>
          <AuthGate>
            <AreaGate area="automacoes" />
          </AuthGate>
        </Route>
        <Route path="/estudio" nest>
          <AuthGate>
            <AreaGate area="estudio" />
          </AuthGate>
        </Route>
        <Route path="/palco" nest>
          <AuthGate>
            <AreaGate area="palco" />
          </AuthGate>
        </Route>
        <Route path="/equipe/:aba?">
          <AuthGate>
            <Boundary id="equipe">
              <EquipePage />
            </Boundary>
          </AuthGate>
        </Route>
        <Route path="/conta/:aba?">
          <AuthGate>
            <Boundary id="conta">
              <ContaPage />
            </Boundary>
          </AuthGate>
        </Route>
        <Route>
          <LegacyOrNotFound />
        </Route>
      </Switch>
    </StepUpProvider>
  );
}
